import React from "react";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";

// Canonical feature rows (stable ordering + human-friendly labels).
// Anything new coming from the backend automatically appears in an "Other" section below.
const FEATURE_COMPARISON_MAP = [
  { key: "premium_templates_level", label: "Premium Template Level", type: "number" },
  { key: "remove_branding", label: "Remove Branding", type: "boolean" },
  { key: "ai_messages_per_month", label: "AI Assistant Messages / Month", type: "number" },
  { key: "priority_support", label: "Priority Support", type: "boolean" },
];

const EXCLUDED_AUTO_FEATURE_KEYS = new Set([
  // Shown explicitly in the table header rows:
  "max_websites",
  "max_published_websites",
  "max_storage_mb",
  // Legacy / derived keys we don't want to surface:
  "premium_templates",
]);

const CheckIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300 dark:text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const formatStorage = (mb) => {
  if (mb >= 1000) return `${(mb / 1000).toFixed(0)}GB`;
  return `${mb}MB`;
};

const formatWebsites = (count) => (count === -1 ? "Unlimited" : count.toString());

const titleizeSnake = (key) =>
  String(key)
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const getFeatureValueType = (value) => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (typeof value === "string") return "string";
  return "unknown";
};

const renderFeatureCell = ({ value, type }) => {
  if (type === "boolean") return value ? <CheckIcon /> : <XIcon />;
  if (type === "number") {
    if (value === null || value === undefined) return <span className={`text-xs sm:text-sm ${textColors.secondary}`}>—</span>;
    return <span className={`text-xs sm:text-sm ${textColors.secondary}`}>{String(value)}</span>;
  }
  if (type === "string") {
    if (!value) return <span className={`text-xs sm:text-sm ${textColors.secondary}`}>—</span>;
    return <span className={`text-xs sm:text-sm ${textColors.secondary}`}>{String(value)}</span>;
  }
  return <span className={`text-xs sm:text-sm ${textColors.secondary}`}>—</span>;
};

const FeatureComparison = ({ plans }) => {
  return (
    <div className={`rounded-lg shadow-sm ${surfaces.primary} border ${borders.default} overflow-hidden`}>
      <div className={`px-4 sm:px-6 py-4 border-b ${borders.default}`}>
        <h2 className={`font-heading text-lg sm:text-xl font-bold ${textColors.primary}`}>Feature Comparison</h2>
        <p className={`text-xs sm:text-sm ${textColors.secondary} mt-1`}>Compare all features across our subscription plans.</p>
      </div>

      <div className="overflow-x-auto -mx-px">
        <table className="w-full min-w-[320px] sm:min-w-[420px]">
          <thead className={surfaces.secondary}>
            <tr>
              <th className={`px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold ${textColors.primary} tracking-wider uppercase`}>Features</th>
              {plans.map((plan) => (
                <th key={plan.name} className={`px-1 sm:px-6 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-semibold ${textColors.primary} tracking-wider uppercase`}>
                  {plan.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={surfaces.primary}>
            <tr className={`border-b ${borders.light}`}>
              <td className={`px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium ${textColors.primary}`}>Website Drafts</td>
              {plans.map((plan) => (
                <td key={plan.name} className={`px-1 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm ${textColors.secondary} text-center`}>
                  {formatWebsites(plan.features.max_websites)}
                </td>
              ))}
            </tr>
            <tr className={`border-b ${borders.light}`}>
              <td className={`px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium ${textColors.primary}`}>Published Websites</td>
              {plans.map((plan) => (
                <td key={plan.name} className={`px-1 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm ${textColors.secondary} text-center`}>
                  {formatWebsites(plan.features.max_published_websites)}
                </td>
              ))}
            </tr>
            <tr className={`border-b ${borders.light}`}>
              <td className={`px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium ${textColors.primary}`}>Storage</td>
              {plans.map((plan) => (
                <td key={plan.name} className={`px-1 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm ${textColors.secondary} text-center`}>
                  {formatStorage(plan.features.max_storage_mb)}
                </td>
              ))}
            </tr>

            {FEATURE_COMPARISON_MAP.map((feature) => (
              <tr key={feature.key} className={`border-b ${borders.light}`}>
                <td className={`px-2 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium ${textColors.primary}`}>{feature.label}</td>
                {plans.map((plan) => (
                  <td key={plan.name} className="px-1 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center">{renderFeatureCell({ value: plan.features?.[feature.key], type: feature.type })}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeatureComparison;



